import { eventNameSchema } from './EventName';
import { matchKeyTSchema } from './MatchKeys';
import { offlineEventContentsSchema } from './OfflineEventContents';
import { z } from 'zod';

const ONE_DAY_SEC = 24 * 60 * 60;

export const jsonDictSchema = z.record(z.any(), {
  invalid_type_error: 'Event custom_data must be a JSON dictionary.',
});

export type JSONDict = z.infer<typeof jsonDictSchema>;

export const moneyDecimalPlaceValidation = /^-?\d+(\b|(\.\d{1,2}))$/;

/**
 * Facebook Offline event, which we (MM) also use as our "generic" event
 * type into the ingestor.
 */
export const offlineEventSchema = z.object({
  event_time: z
    .number()
    .int()
    .min(
      new Date('2010-01-01').valueOf() / 1000, // UNIX timestamp
      {
        message: 'Events must be from year 2010 or later.',
      },
    )
    .max(new Date().valueOf() / 1000 + ONE_DAY_SEC, {
      message: 'Events cannot be more than 24 hours in the future.',
    }),
  event_name: eventNameSchema,
  currency: z
    .string({
      invalid_type_error: 'Event currency can only contain letters.',
    })
    .length(3, {
      message: 'Event currency must be exactly three letters.',
    })
    .optional(), // Required for event_type == "Purchase".
  value: z
    .number({
      invalid_type_error:
        'Event value must be a plain positive number (no commas, no $)',
    })
    .gte(0, { message: 'Event value must be a positive number.' })
    .transform((value) => parseFloat(value.toFixed(2)))
    .optional(), // Required for event_type == "Purchase".
  content_type: z
    .string({
      invalid_type_error: 'Event content_type must be alphanumeric.',
    })
    .optional(),
  custom_data: jsonDictSchema.optional(),
  order_id: z
    .string({
      invalid_type_error: 'Event order_id must be alphanumeric.',
    })
    .optional(),
  match_keys: matchKeyTSchema.optional(),
  contents: offlineEventContentsSchema.array().optional(),
  direct_attribution: z
    .boolean({ invalid_type_error: 'Invalid drive driven attribution flag.' })
    .default(false),
});

export type OfflineEvent = z.infer<typeof offlineEventSchema>;
export type OfflineEventInput = z.input<typeof offlineEventSchema>;

export const offlineEventNameWithCountSchema = z.object({
  eventName: eventNameSchema,
  count: z.number(),
});

export type OfflineEventNameWithCount = z.infer<
  typeof offlineEventNameWithCountSchema
>;

export const offlineEventWithMatchKeysSchema = z.object({
  event_name: z.string(),
  event_time: z.number(),
  match_keys: z
    .object({
      key: z.string(),
      value: z.string(),
    })
    .array(),
});

export type OfflineEventWithMatchKeys = z.infer<
  typeof offlineEventWithMatchKeysSchema
>;

/**
 * More 'flexible' offline event that allows more time formats
 * that ingest will convert to unix time.
 */

// transform
export const flexibleOfflineEventSchema = offlineEventSchema
  .omit({ event_time: true })
  .extend({ event_time: z.string().or(z.number()) });
export type FlexibleOfflineEvent = z.infer<typeof flexibleOfflineEventSchema>;

export const facebookUploadableOfflineEventSchema = offlineEventSchema.omit({
  direct_attribution: true,
});
export type FacebookUploadableOfflineEvent = z.infer<
  typeof facebookUploadableOfflineEventSchema
>;
